import { Inject, Injectable } from '@angular/core';
import { ScreenSizeDetectorService } from '@serviceplan/ui-kit';
import { ClientEnvironment, ENVIRONMENT, GuidedTourConfig, InformationBannerService, UserPreferencesService } from '@thehubz/client/core';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { TourService } from 'ngx-ui-tour-md-menu';
import { BehaviorSubject } from 'rxjs';

const stepHiddenClassName = 'guided-tour-step-hidden';

@Injectable({
  providedIn: 'root',
})
export class GuidedTourService {
  private STORAGE_KEY_ENDED = 'guidedTourEnded';

  private config!: GuidedTourConfig | undefined;
  private hasEnded$ = new BehaviorSubject<string | null>(null);

  isInitialized$ = new BehaviorSubject<boolean>(false);

  isRunning = false;
  currentStep: AnyType = null;
  steps: AnyType[] = [];

  options: AnyType = {
    popoverClass: 'guided-tour_popover',
    enableBackdrop: true,
    disablePageScrolling: true,
    duplicateAnchorHandling: 'registerFirst',
    endBtnTitle: `Done`,
    prevBtnTitle: `Back`,
    nextBtnTitle: `Next`,
    smoothScroll: false, // necessary to align the anchor combination properly
    isOptional: true, // default to true so that if an anchor is not found, the tour continues
    stepDimensions: {
      minWidth: '20rem',
      maxWidth: '50rem',
      width: 'auto',
    },
  };

  readonly brandingKey!: CustomClients;
  readonly CustomClients = CustomClients;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private tourService: TourService,
    private userPreferencesService: UserPreferencesService,
    private bannerService: InformationBannerService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {
    // Fix for tour module under Angular 16
    (this.tourService as AnyType)['subscribeToNavigationStartEvent'] = () => {
      return 'Prevent linter error: "Unexpected empty arrow function"';
    };

    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    document.addEventListener('initLogin', () => {
      this.reset();
    });
  }

  async init(steps: AnyType[], options: AnyType) {
    if (this.isInitialized$.getValue()) return;

    this.config = await this.userPreferencesService.getGuidedTourConfig();
    this.hasEnded$.next(localStorage.getItem(this.STORAGE_KEY_ENDED));

    if (steps && steps.length) {
      this.steps = steps;
    }

    if (options) {
      this.options = Object.assign({}, this.options, options);
    }

    this.tourService.initialize$.subscribe((steps: AnyType[]) => {
      this.isInitialized$.next(true);

      if (this.hasEnded$.getValue() || this?.config?.skip) return;

      this.start();
    });

    this.tourService.end$.subscribe(() => {
      this.onEnd();
    });

    this.tourService.stepShow$.subscribe((current: AnyType) => {
      this.onShowStep(current.step);
    });

    this.tourService.stepHide$.subscribe((current: AnyType) => {
      this.onHideStep(current.step);
    });

    this.tourService.initialize(this.steps, this.options);
  }

  start(stepIndex = 0): void {
    this.bannerService.pause();
    if (this.isRunning || this.screenSizeDetectorService.isSmallScreen) return;
    this.tourService.startAt(stepIndex);
    this.onStart();
  }

  end(): void {
    this.tourService.end();
    this.onEnd();
  }

  async onShowStep(step: AnyType) {
    const anchorId = step.anchorId;
    const anchorIndex = this.getStepIndexByAnchorId(anchorId);
    const animationTimeout = 300;

    document.body.dataset['currentTourStep'] = anchorId;

    this.currentStep = step;

    if (step.anchorId.includes('vorwerk.asset-filter-iterative')) {
      await this.updateStepPosition();

      setTimeout(async () => {
        // needs to be executed twice to get proper position
        await this.updateStepPosition();
        document.body.classList.remove(stepHiddenClassName);
      }, animationTimeout);
    } else {
      await this.updateStepPosition();
      setTimeout(() => {
        document.body.classList.remove(stepHiddenClassName);
      }, animationTimeout);
    }

    if (['asset-grid-item-quick-action.anchor.toggle', 'asset-list-item-quick-action.anchor.toggle'].includes(step.anchorId)) {
      this.adjustPopoverPosition(anchorId, this.currentStep.placement);
    }

    if (/\.anchor\.(toggle|click)/.test(anchorId)) {
      const selector = `[touranchor="${anchorId}"]`;
      const targetEl = document.querySelector(selector) as HTMLElement | undefined;
      const controledEl = document.querySelector(`[data-guided-tour-id="${targetEl?.dataset?.['guidedTourControls']}"]`);
      const buttonEl = (controledEl || targetEl?.closest('button')) as HTMLButtonElement | undefined;

      if (buttonEl?.hasAttribute('aria-expanded') && buttonEl?.getAttribute('aria-expanded') === 'true') {
        return;
      }

      buttonEl?.click();
    }

    if (anchorIndex === 0) {
      this.onShowFirstStep();
    }
  }

  onHideStep(step: AnyType) {
    document.body.classList.add(stepHiddenClassName);
    delete document.body.dataset['currentTourStep'];

    const anchorId = step.anchorId;
    this.currentStep = null;
    this.clearPopoverPosition();

    if (/\.anchor\.toggle/.test(anchorId)) {
      const selector = `[touranchor="${anchorId}"]`;
      const buttonEl = document.querySelector(selector) as HTMLButtonElement | undefined;
      const cdkOverlayBackdrop = document.querySelector('.cdk-overlay-backdrop') as HTMLElement | undefined;

      buttonEl?.click();
      cdkOverlayBackdrop?.click();
    }

    if (anchorId === 'assets-filter.anchor') {
      const filterBtn = document.querySelector('[touranchor="assets-filter.anchor"]') as HTMLButtonElement | undefined;
      const isExpanded = filterBtn?.getAttribute('aria-expanded') === 'true';
      if (!isExpanded) filterBtn?.click();
    }
  }

  onStart(): void {
    document.body.classList.add('guided-tour-active');
    this.isRunning = true;
  }

  onEnd(): void {
    this.bannerService.resume();

    document.body.classList.remove('guided-tour-active');
    const endDateString = new Date().toISOString();
    localStorage.setItem(this.STORAGE_KEY_ENDED, endDateString);
    this.hasEnded$.next(endDateString);
    this.isRunning = false;
  }

  onShowFirstStep(): void {
    setTimeout(() => {
      this.createSkipCheckbox();
    });
  }

  createSkipCheckbox() {
    const checkboxName = 'skip-tour-checkbox';
    let checkboxEl = document.querySelector(`[name="${checkboxName}"]`);

    if (checkboxEl) return;

    const label = this.options.skipText || 'Do not show the tour again';

    const checkboxHtml = /* html */ `
      <label class="skip-tour mt-6 flex items-center gap-2">
        <input type="checkbox" name="${checkboxName}" class="checkbox-native" ${this.config?.skip && 'checked'}> ${label}
      </label>
    `;

    const popoverContentEl = document.querySelector('.mat-mdc-card-content') as HTMLElement;

    if (popoverContentEl) {
      popoverContentEl.insertAdjacentHTML('beforeend', checkboxHtml);

      setTimeout(() => {
        checkboxEl = document.querySelector(`[name="${checkboxName}"]`);

        checkboxEl?.addEventListener('change', (event: AnyType) => {
          this.userPreferencesService.setGuidedTourConfig({ skip: event?.target?.checked });
        });
      });
    }
  }

  async updateStepPosition() {
    this.clearPopoverPosition();
    this.clearBackdropPosition();

    this.combineAnchors(this.currentStep.anchorId);

    await this.adjustAnchorPosition(this.currentStep.anchorId);
    await this.adjustBackdropPosition(this.currentStep.anchorId);
    await this.adjustPopoverPosition(this.currentStep.anchorId, this.currentStep.placement);
  }

  /*
   ** Combines multiple anchors that share the same anchorId and applies the dimensions of all of them to the first anchor
   */
  combineAnchors(anchorId: string): void {
    const anchors = this.getAnchorsByAnchorId(anchorId).filter((anchor: HTMLElement) => anchor.classList.contains('combined-anchor'));

    if (anchors.length < 2) return;

    const { top, bottom } = this.getCombinedAnchorPosition(anchorId);
    const anchorHeight = bottom - top;

    anchors[0].style.height = `${anchorHeight}px`;
  }

  adjustAnchorPosition(anchorId: string): Promise<void> {
    if (!['asset-grid-item-quick-action.anchor.toggle', 'asset-list-item-quick-action.anchor.toggle'].includes(anchorId)) return Promise.resolve();

    return new Promise((resolve) => {
      const anchorEl = document.querySelector(`[touranchor="${anchorId}"]`) as HTMLElement | undefined;
      const scrollParent = document.querySelector(`.app-content-container`) as HTMLElement | undefined;
      const assetActions = document.querySelectorAll('thehubz-asset-gallery-card-actions button');
      let firstVisibleAction: AnyType;

      const scrollBox = scrollParent?.getBoundingClientRect();

      if (!anchorEl || !scrollParent || !scrollBox) {
        resolve();
        return;
      }

      for (const action of assetActions) {
        const actionBox = action.getBoundingClientRect();
        const isVisible = actionBox.top >= scrollBox.top && actionBox.bottom <= scrollBox.bottom;

        if (isVisible) {
          firstVisibleAction = action;
          break;
        }
      }

      if (!firstVisibleAction) {
        resolve();
        return;
      }

      anchorEl.style.left = '';
      anchorEl.style.top = '';
      anchorEl.style.width = '';
      anchorEl.style.height = '';

      setTimeout(() => {
        const anchorBox = anchorEl.getBoundingClientRect();
        const actionBox = firstVisibleAction.getBoundingClientRect();

        const top = actionBox.top - anchorBox.top;
        const left = actionBox.left - anchorBox.left;

        const offset = 10;

        anchorEl.style.left = `${left - offset}px`;
        anchorEl.style.top = `${top - offset}px`;
        anchorEl.style.width = `${firstVisibleAction.offsetWidth}px`;
        anchorEl.style.height = `${firstVisibleAction.offsetHeight}px`;

        resolve();
      });
    });
  }

  adjustPopoverPosition(anchorId: string, placement: string): Promise<void> {
    return new Promise((resolve) => {
      const popoverEl = this.getPopoverElement();
      const anchorEl = document.querySelector(`[touranchor="${anchorId}"]`) as HTMLElement;

      if (!popoverEl || !anchorEl) return;

      this.clearPopoverPosition();

      setTimeout(() => {
        const anchorPosition = this.getCombinedAnchorPosition(anchorId);
        const popoverBox = popoverEl.getBoundingClientRect();
        const anchorBox = anchorEl.getBoundingClientRect();
        const alignTo = placement || '';
        let offsetX = 0;
        let offsetY = 0;

        if (alignTo.includes('fit-vertically')) {
          offsetY =
            anchorPosition.top > window.innerHeight - anchorPosition.bottom ? anchorBox.top - popoverBox.bottom : Math.abs(popoverBox.top - anchorBox.bottom);
        } else if (alignTo.includes('top')) {
          offsetY = anchorBox.top - popoverBox.bottom;
        } else if (alignTo.includes('bottom')) {
          offsetY = Math.abs(popoverBox.top - anchorBox.bottom);
        }

        if (alignTo.includes('fit-horizontally')) {
          offsetY = anchorPosition.left > window.innerWidth - anchorPosition.right ? anchorBox.left - popoverBox.right : anchorBox.right - popoverBox.left;
        } else if (alignTo.includes('left')) {
          offsetX = anchorBox.left - popoverBox.right;
        } else if (alignTo.includes('right')) {
          offsetX = anchorBox.right - popoverBox.left;
        }

        // Applied to the card due to the fact that the style of the popover will be overwritten
        popoverEl.style.transform = `translate(${offsetX}px, ${offsetY}px)`;

        setTimeout(resolve);
      }, 0);
    });
  }

  clearPopoverPosition(): void {
    const popoverEl = this.getPopoverElement();

    if (popoverEl) popoverEl.style.transform = ``;
  }

  adjustBackdropPosition(anchorId: string): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const backdropAreas = [...document.querySelectorAll('.ngx-ui-tour_backdrop')] as HTMLElement[];

        if (!backdropAreas.length) return;

        const { top, bottom } = this.getCombinedAnchorPosition(anchorId);

        const backdropTop = backdropAreas[1];
        const backdropBottom = backdropAreas[2];
        const backdropBottomHeight = window.innerHeight - bottom;

        backdropTop.style.height = `${top}px`;

        backdropBottom.style.top = `${bottom}px`;
        backdropBottom.style.height = `${backdropBottomHeight}px`;

        resolve();
      });
    });
  }

  clearBackdropPosition() {
    const backdropAreas = [...document.querySelectorAll('.ngx-ui-tour_backdrop')] as HTMLElement[];

    if (!backdropAreas.length) return;
  }

  getPopoverElement() {
    const popoverEls = [...document.querySelectorAll('.tour-step')] as HTMLElement[];
    const popoverEl = popoverEls.find((el: AnyType) => el.querySelector('.mat-mdc-card')) as HTMLElement;

    return popoverEl;
  }

  getAnchorsByAnchorId(anchorId: string) {
    const anchors = [...document.querySelectorAll(`[touranchor="${anchorId}"]`)] as HTMLElement[];

    return anchors;
  }

  getStepIndexByAnchorId(anchorId: string): number {
    return this.steps.findIndex((step: AnyType) => step.anchorId === anchorId);
  }

  getCombinedAnchorPosition(anchorId: string) {
    let anchors = this.getAnchorsByAnchorId(anchorId || this.currentStep.anchorId);

    if (this.currentStep.duplicateAnchorHandling === 'registerFirst' && !anchorId.includes('.combined')) {
      anchors = anchors.filter((anchor, index) => index === 0);
    }

    const position = {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    };

    if (anchors.length < 1) return position;

    const firstAnchorBox = anchors[0].getBoundingClientRect();
    const lastAnchorBox = anchors.at(-1)?.getBoundingClientRect();

    position.top = firstAnchorBox.top;
    position.bottom = lastAnchorBox?.bottom || firstAnchorBox.bottom;
    position.left = Math.min(firstAnchorBox.left, lastAnchorBox?.left || Infinity);
    position.right = Math.max(firstAnchorBox.right, lastAnchorBox?.right || 0);

    return position;
  }

  private _debugAnchorPosition() {
    const className = 'tour-debug-div';
    const pos = this.getCombinedAnchorPosition(this.currentStep.anchorId);
    let div = document.querySelector(`.${className}`) as HTMLElement;

    if (!div) {
      div = document.createElement('div');
      div.className = className;
      document.body.append(div);
    }

    div.style.background = 'rgb(255 0 0 / 30%)';
    div.style.position = 'fixed';
    div.style.inset = `${pos.top}px ${window.innerWidth - pos.right}px ${window.innerHeight - pos.bottom}px ${pos.left}px`;
    div.style.pointerEvents = 'none';
    div.style.zIndex = '9999999999999999999';
  }

  reset() {
    localStorage.removeItem(this.STORAGE_KEY_ENDED);
  }
}
